import { RotateCcw, SlidersHorizontal } from "lucide-react";
import type { ScenarioInputs } from "../lib/scenario";
import { defaultScenario, formatCurrency, formatNumber, modelScenario } from "../lib/scenario";

type ScenarioLabProps = {
  scenario: ScenarioInputs;
  setScenario: (scenario: ScenarioInputs) => void;
};

type RangeProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (value: number) => void;
};

function RangeControl({ label, value, min, max, step = 1, suffix = "", onChange }: RangeProps) {
  return (
    <label className="range-control">
      <span>
        {label}
        <strong>{suffix === "EUR" ? formatCurrency(value) : `${formatNumber(value)}${suffix}`}</strong>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
      />
    </label>
  );
}

export default function ScenarioLab({ scenario, setScenario }: ScenarioLabProps) {
  const output = modelScenario(scenario);
  const setField = (key: keyof ScenarioInputs, value: number) => setScenario({ ...scenario, [key]: value });
  const comparison = [
    { label: "Conservative", budget: scenario.budget * 0.7, adoption: Math.max(28, scenario.adoptionProbability - 18), color: "clay" },
    { label: "Base case", budget: scenario.budget, adoption: scenario.adoptionProbability, color: "gold" },
    { label: "Accelerated", budget: scenario.budget * 1.35, adoption: Math.min(92, scenario.adoptionProbability + 14), color: "green" },
  ].map((item) => {
    const modeled = modelScenario({ ...scenario, budget: item.budget, adoptionProbability: item.adoption });
    return { ...item, modeled };
  });

  return (
    <section id="scenario" className="section scenario-section">
      <div className="section-heading">
        <div>
          <span className="section-index">03</span>
          <h2>Scenario Analysis Lab</h2>
        </div>
        <p>Predictive analysis links budget, language coverage, validation intensity and policy adoption to cost per successful navigation, benefit-cost ratio and scale readiness.</p>
      </div>

      <div className="scenario-grid">
        <div className="scenario-controls">
          <div className="panel-title">
            <h3><SlidersHorizontal size={18} /> Inputs</h3>
            <button type="button" onClick={() => setScenario(defaultScenario)}><RotateCcw size={15} /> Reset</button>
          </div>
          <RangeControl label="Budget" value={scenario.budget} min={60000} max={420000} step={5000} suffix="EUR" onChange={(value) => setField("budget", value)} />
          <RangeControl label="Communities" value={scenario.communities} min={30} max={360} step={5} onChange={(value) => setField("communities", value)} />
          <RangeControl label="Language packs" value={scenario.languagePacks} min={4} max={28} onChange={(value) => setField("languagePacks", value)} />
          <RangeControl label="Field validation intensity" value={scenario.validationIntensity} min={20} max={100} suffix="%" onChange={(value) => setField("validationIntensity", value)} />
          <RangeControl label="Policy adoption probability" value={scenario.adoptionProbability} min={20} max={95} suffix="%" onChange={(value) => setField("adoptionProbability", value)} />
          <RangeControl label="Estimated public value per successful navigation" value={scenario.serviceValue} min={20} max={140} suffix="EUR" onChange={(value) => setField("serviceValue", value)} />
        </div>

        <div className="scenario-results">
          <div className="result-card featured">
            <span>Benefit-cost ratio</span>
            <strong>{output.benefitCostRatio.toFixed(2)}</strong>
            <p>{output.benefitCostRatio >= 1 ? "Positive expected public value" : "Below public-value threshold"}</p>
          </div>
          <div className="result-card">
            <span>Cost per successful navigation</span>
            <strong>{formatCurrency(output.costPerSuccess)}</strong>
            <p>{formatNumber(output.successfulNavigations)} successful navigations</p>
          </div>
          <div className="result-card">
            <span>Risk-adjusted readiness</span>
            <strong>{output.readiness}/100</strong>
            <p>Evidence power {output.evidencePower}/100</p>
          </div>
          <div className="result-card">
            <span>Net expected benefit</span>
            <strong>{formatCurrency(output.netBenefit)}</strong>
            <p>{formatNumber(output.expectedReach)} expected user interactions</p>
          </div>
        </div>

        <div className="scenario-chart viz-panel">
          <div className="panel-title">
            <h3>Model Sensitivity</h3>
            <span>Readiness drivers</span>
          </div>
          <div className="sensitivity-list">
            {output.sensitivity.map((item) => (
              <div key={item.label}>
                <span>{item.label}</span>
                <i><b style={{ width: `${item.value}%` }} /></i>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="scenario-table viz-panel">
          <div className="panel-title">
            <h3>Policy Scenario Comparison</h3>
            <span>Econometric planning view</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Budget</th>
                <th>B-C ratio</th>
                <th>Cost / success</th>
                <th>Readiness</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((item) => (
                <tr key={item.label} className={`row-${item.color}`}>
                  <td>{item.label}</td>
                  <td>{formatCurrency(item.budget, true)}</td>
                  <td>{item.modeled.benefitCostRatio.toFixed(2)}</td>
                  <td>{formatCurrency(item.modeled.costPerSuccess)}</td>
                  <td>{item.modeled.readiness}/100</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="method-note">
            Model structure: successful navigation is estimated from reachable communities, adoption probability, validation intensity, language-pack depth and evidence reliability. Benefits use estimated public value per successful navigation with a modest social externality multiplier.
          </p>
        </div>
      </div>
    </section>
  );
}
